import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, LayoutList, LayoutDashboard, Filter, Link2 } from 'lucide-react'
import { format } from 'date-fns'
import { projectsApi } from '../api/projects'
import { issuesApi } from '../api/issues'
import { useAuthStore } from '../store/authStore'
import { canCreateMainIssue, canMoveIssue, hasFullAccess } from '../lib/roles'
import { copyTextToClipboard } from '../lib/clipboard'
import { PriorityBadge } from '../components/common/PriorityBadge'
import { Avatar } from '../components/common/Avatar'
import { Button } from '../components/common/Button'
import IssueForm, { type CreateIssuePayload } from '../components/issues/IssueForm'
import IssueFiltersBar from '../components/issues/IssueFiltersBar'
import ReadyForNextStageModal from '../components/issues/ReadyForNextStageModal'
import IssueSheetActions from '../components/issues/IssueSheetActions'
import { insertIssueIntoProjectCaches } from '../lib/issueCache'
import { stripPendingInlineMedia } from '../lib/issueDescription'
import { finalizeIssueAttachmentsAndInlineMedia } from '../lib/issueSubmission'
import { invalidateIssueViews } from '../lib/queryInvalidation'
import { requiresStatusDetailsPopup } from '../lib/readyForNextStage'
import { submitStatusTransitionWithInternalNote } from '../lib/statusTransitionSubmission'
import type { IssueFilters } from '../api/issues'
import type { Issue, IssueStatus } from '../types'

function initialsFromName(name?: string): string {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?'
}

const statusColors: Record<string, string> = {
  TODO: '#94a3b8',
  IN_PROGRESS: '#6366f1',
  DONE: '#22c55e',
}

function StatusDot({ status }: { status?: IssueStatus }) {
  const color = status ? statusColors[status.category] || status.color : '#94a3b8'
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  )
}

function IssueRow({
  issue,
  onClick,
  canDrag = false,
}: {
  issue: Issue
  onClick: () => void
  canDrag?: boolean
}) {
  const [hovered, setHovered] = useState(false)
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: issue.id,
    disabled: !canDrag,
  })

  const creatorName = issue.createdBy?.fullName || 'Unknown'

  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(260px, 1fr) 140px 160px 160px 120px 120px 100px',
        alignItems: 'center',
        gap: '12px',
        padding: '9px 20px',
        background: hovered ? '#f8fafc' : '#ffffff',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        transition: 'background 0.1s',
        transform: CSS.Transform.toString(transform),
        ...(transition ? { transition } : {}),
      }}
      {...listeners}
      {...attributes}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <StatusDot status={issue.status} />
        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>
          {issue.identifier}
        </span>
        <span
          style={{
            fontSize: '13.5px',
            color: '#1e293b',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {issue.title}
        </span>
        {(issue._count?.children ?? 0) > 0 && (
          <span style={{ fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>
            {issue._count!.children} sub
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, minWidth: 0 }}>
        {issue.labels.length > 0 ? (
          <>
            {issue.labels.map(({ label }) => (
              <span
                key={label.id}
                title={label.name}
                style={{ maxWidth: 105, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '1px 5px', borderRadius: 4, border: '1px solid ' + label.color + '44', background: label.color + '18', color: label.color, fontSize: 9, fontWeight: 700, lineHeight: '14px' }}
              >
                {label.name}
              </span>
            ))}
          </>
        ) : (
          <span style={{ fontSize: 11, color: '#cbd5e1' }}>-</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#e2e8f0',
            color: '#475569',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initialsFromName(creatorName)}
        </span>
        <span
          style={{
            fontSize: '12px',
            color: '#475569',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {creatorName}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '4px' }}>
        {issue.assignees.slice(0, 3).map((assignee) => (
          <Avatar key={assignee.user.id} user={assignee.user} size="sm" />
        ))}
        {issue.assignees.length === 0 && (
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>Unassigned</span>
        )}
      </div>
      <PriorityBadge priority={issue.priority} size="sm" />
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        {issue.status?.name || '-'}
      </div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        {issue.dueDate ? format(new Date(issue.dueDate), 'MMM d') : '-'}
      </div>
    </div>
  )
}

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const [createOpen, setCreateOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [filters, setFilters] = useState<IssueFilters>({})
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle')
  const [readyTransition, setReadyTransition] = useState<{
    issue: Issue
    targetStatusId: string
    targetIndex: number
  } | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.getProject(projectId!),
    enabled: !!projectId,
  })

  const { data: statusesData } = useQuery({
    queryKey: ['statuses', projectId],
    queryFn: () => projectsApi.listStatuses(projectId!),
    enabled: !!projectId,
  })

  const { data: membersData } = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => projectsApi.listMembers(projectId!),
    enabled: !!projectId,
  })

  const { data: issuesData, isLoading } = useQuery({
    queryKey: ['issues', projectId, { statusId: statusFilter }, filters],
    queryFn: () => issuesApi.listIssues(projectId!, {
      ...filters,
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    enabled: !!projectId,
  })

  const { data: labelsData } = useQuery({
    queryKey: ['labels', projectId],
    queryFn: () => projectsApi.listLabels(projectId!),
    enabled: !!projectId,
  })

  const createMutation = useMutation({
    mutationFn: async ({ attachments, inlineMedia, ...data }: CreateIssuePayload) => {
      const created = await issuesApi.createIssue(projectId!, {
        ...data,
        description: stripPendingInlineMedia(data.description),
      } as never)
      const issueId = created.data.data.id
      const finalDescription = await finalizeIssueAttachmentsAndInlineMedia({
        issueId,
        description: data.description,
        attachments,
        inlineMedia,
      })
      if (finalDescription !== data.description) {
        return issuesApi.updateIssue(issueId, { description: finalDescription } as never)
      }
      return created
    },
    onSuccess: async (response) => {
      insertIssueIntoProjectCaches(qc, projectId!, response.data.data)
      await Promise.all([
        invalidateIssueViews(qc, projectId!),
        qc.refetchQueries({ queryKey: ['issues', projectId!] }),
      ])
      setCreateOpen(false)
    },
  })

  const reorderMutation = useMutation({
    mutationFn: ({ issueId, targetStatusId, targetIndex }: { issueId: string; targetStatusId: string; targetIndex: number }) =>
      issuesApi.reorderIssue(projectId!, { issueId, targetStatusId, targetIndex }),
    onSuccess: async () => {
      await invalidateIssueViews(qc, projectId!)
    },
  })

  const readyForNextStageMutation = useMutation({
    mutationFn: ({ issueId, issueTitle, targetStatusName, targetStatusId, targetIndex, labelIds, timeLog, proof }: {
      issueId: string
      issueTitle: string
      targetStatusName: string
      targetStatusId: string
      targetIndex: number
      labelIds?: string[]
      timeLog?: Parameters<typeof issuesApi.readyForNextStage>[1]['timeLog']
      proof?: Parameters<typeof submitStatusTransitionWithInternalNote>[0]['payload']['proof']
    }) => submitStatusTransitionWithInternalNote({
      issueId,
      issueTitle,
      targetStatusName,
      payload: { targetStatusId, labelIds, timeLog, proof },
    }),
    onSuccess: async () => {
      setReadyTransition(null)
      await invalidateIssueViews(qc, projectId!)
    },
  })

  const project = projectData?.data?.data
  const statuses = (statusesData?.data?.data || []).slice().sort((a, b) => a.position - b.position)
  const labels = labelsData?.data?.data || []
  const members = membersData?.data?.data || []
  const issues: Issue[] = (issuesData?.data?.data || [])
    .slice()
    .sort((a, b) => a.position - b.position || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  const canManageMainIssues = hasFullAccess(user?.role)
  const canCreateMainIssues = canCreateMainIssue(user?.role)
  const visibleStatuses = statusFilter ? statuses.filter((status) => status.id === statusFilter) : statuses

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return

    const issueId = active.id as string
    const overIssueId = over.id as string
    if (issueId === overIssueId) return

    const draggedIssue = issues.find((issue) => issue.id === issueId)
    const targetIssue = issues.find((issue) => issue.id === overIssueId)
    if (!draggedIssue || !targetIssue) return
    if (!canMoveIssue(user?.role, draggedIssue, user?.id)) return

    const targetStatus = statuses.find((status) => status.id === targetIssue.statusId)
    if (!targetStatus) return

    const targetStatusIssues = issues.filter((issue) => issue.statusId === targetIssue.statusId)
    const targetIndex = targetStatusIssues.findIndex((issue) => issue.id === targetIssue.id)
    if (targetIndex === -1) return

    if (requiresStatusDetailsPopup({
      currentStatusName: draggedIssue.status?.name,
      nextStatusName: targetStatus.name,
      userRole: user?.role,
    })) {
      setReadyTransition({
        issue: draggedIssue,
        targetStatusId: targetStatus.id,
        targetIndex,
      })
      return
    }

    reorderMutation.mutate({
      issueId,
      targetStatusId: targetStatus.id,
      targetIndex,
    })
  }

  const handleCopyLink = async () => {
    try {
      await copyTextToClipboard(window.location.href)
      setCopyState('copied')
      window.setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      window.alert('Could not copy the project link from this browser.')
    }
  }

  return (
    <div style={{ padding: '24px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {project && (
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: project.color,
                display: 'inline-block',
              }}
            />
          )}
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
            {project?.name || 'Project'}
          </h1>
          <span style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
            {project?.identifier}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button variant="secondary" size="sm" onClick={handleCopyLink}>
            <Link2 size={14} /> {copyState === 'copied' ? 'Link copied' : 'Copy link'}
          </Button>
          <IssueSheetActions
            projectId={projectId!}
            projectIdentifier={project?.identifier}
            canImport={canManageMainIssues}
          />
          <Link
            to={`/projects/${projectId}/list`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              borderRadius: '6px',
              background: '#6366f1',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            <LayoutList size={14} /> List
          </Link>
          <Link
            to={`/projects/${projectId}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              borderRadius: '6px',
              background: '#f1f5f9',
              color: '#475569',
              fontSize: '13px',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            <LayoutDashboard size={14} /> Board
          </Link>

          {canCreateMainIssues && (
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus size={14} /> New Issue
            </Button>
          )}
        </div>
      </div>

      <IssueFiltersBar
        filters={filters}
        onChange={setFilters}
        members={members}
        labels={labels}
        currentUserId={user?.id}
        resultCount={issues.length}
      />

      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', alignItems: 'center' }}>
        <Filter size={14} style={{ color: '#94a3b8' }} />
        <button
          onClick={() => setStatusFilter('')}
          style={{
            padding: '4px 10px',
            fontSize: '12px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            background: !statusFilter ? '#6366f1' : '#f1f5f9',
            color: !statusFilter ? '#fff' : '#475569',
          }}
        >
          All
        </button>
        {statuses.map((status) => (
          <button
            key={status.id}
            onClick={() => setStatusFilter(statusFilter === status.id ? '' : status.id)}
            style={{
              padding: '4px 10px',
              fontSize: '12px',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              background: statusFilter === status.id ? '#6366f1' : '#f1f5f9',
              color: statusFilter === status.id ? '#fff' : '#475569',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <span
              style={{ width: 6, height: 6, borderRadius: '50%', background: status.color, display: 'inline-block' }}
            />
            {status.name}
          </button>
        ))}
      </div>

      <div
        style={{
          background: '#ffffff',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(260px, 1fr) 140px 160px 160px 120px 120px 100px',
            gap: '12px',
            padding: '10px 20px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontSize: '11px',
            fontWeight: 600,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <span>Issue</span>
          <span>Labels</span>
          <span>Creator</span>
          <span>Assignees</span>
          <span>Priority</span>
          <span>Status</span>
          <span>Due</span>
        </div>

        {isLoading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : issues.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 16px' }}>
              No issues yet
            </p>
            {canCreateMainIssues && (
              <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
                <Plus size={14} /> Create first issue
              </Button>
            )}
          </div>
        ) : (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div>
              {visibleStatuses.map((status) => {
                const statusIssues = issues.filter((issue) => issue.statusId === status.id)

                return (
                  <div key={status.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 20px',
                        background: '#fcfcfd',
                        borderBottom: '1px solid #f8fafc',
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: status.color, display: 'inline-block' }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{status.name}</span>
                      <span
                        style={{
                          fontSize: 11,
                          background: '#f1f5f9',
                          color: '#64748b',
                          padding: '1px 6px',
                          borderRadius: 10,
                        }}
                      >
                        {statusIssues.length}
                      </span>
                    </div>

                    {statusIssues.length === 0 ? (
                      <div style={{ padding: '20px', color: '#94a3b8', fontSize: 12 }}>No issues in this status</div>
                    ) : (
                      <SortableContext items={statusIssues.map((issue) => issue.id)} strategy={verticalListSortingStrategy}>
                        {statusIssues.map((issue) => (
                          <IssueRow
                            key={issue.id}
                            issue={issue}
                            canDrag={canMoveIssue(user?.role, issue, user?.id)}
                            onClick={() => navigate(`/issues/${issue.id}`)}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </div>
                )
              })}
            </div>
          </DndContext>
        )}
      </div>

      <IssueForm
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        projectId={projectId!}
        statuses={statuses}
        onSubmit={(data) => createMutation.mutate(data)}
        isSubmitting={createMutation.isPending}
      />

      <ReadyForNextStageModal
        isOpen={!!readyTransition}
        issue={readyTransition?.issue || null}
        statuses={statuses}
        labels={labels}
        user={user}
        initialNextStatusId={readyTransition?.targetStatusId || null}
        lockNextStatus
        isSubmitting={readyForNextStageMutation.isPending}
        onClose={() => setReadyTransition(null)}
        onSubmit={({ targetStatusId, labelIds, timeLog, proof }) => {
          if (!readyTransition) return
          const targetStatus = statuses.find((status) => status.id === targetStatusId)
          if (!targetStatus) return
          readyForNextStageMutation.mutate({
            issueId: readyTransition.issue.id,
            issueTitle: readyTransition.issue.title,
            targetStatusName: targetStatus.name,
            targetStatusId,
            targetIndex: readyTransition.targetIndex,
            ...(labelIds ? { labelIds } : {}),
            ...(timeLog ? { timeLog } : {}),
            ...(proof ? { proof } : {}),
          })
        }}
      />
    </div>
  )
}
